import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../../UserContext';

const Header = () => {
  const { currentUser, userData, logout } = useUser();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      setIsMenuOpen(false);
      setIsProfileDropdownOpen(false);
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
  };

  return (
    <header className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg sticky top-0 z-50">
      <div className="container mx-auto px-4 py-3">
        <div className="flex justify-between items-center">
          {/* Logo/Brand */}
          <Link to="/" className="flex items-center space-x-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-indigo-100">
              FlashGen
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            <Link
              to="/"
              className="hover:bg-white/10 px-4 py-2 rounded-lg transition duration-300 font-medium"
            >
              Home
            </Link>
            <Link
              to="/quiz"
              className="hover:bg-white/10 px-4 py-2 rounded-lg transition duration-300 font-medium"
            >
              Quiz
            </Link>
            <Link
              to="/all-flashcards"
              className="hover:bg-white/10 px-4 py-2 rounded-lg transition duration-300 font-medium"
            >
              Flashcards
            </Link>
            <Link
              to="/create"
              className="hover:bg-white/10 px-4 py-2 rounded-lg transition duration-300 font-medium"
            >
              Create
            </Link>
            <Link
              
              className="bg-white/10 px-4 py-2 rounded-lg transition duration-300 font-medium"
            >
              {userData?.name}
            </Link>

            {/* Profile Dropdown */}
            <div className="relative ml-2">
              <button
                onClick={toggleProfileDropdown}
                className="flex items-center space-x-1 hover:bg-white/10 px-3 py-2 rounded-lg transition duration-300"
              >
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                  <span className="font-medium">
                    {userData?.name?.charAt(0) || currentUser?.email?.charAt(0).toUpperCase() || 'P'}
                  </span>
                </div>
                <svg
                  className={`w-4 h-4 transition-transform duration-200 ${
                    isProfileDropdownOpen ? 'transform rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {isProfileDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                  <Link
                    to="/profile"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50"
                    onClick={() => setIsProfileDropdownOpen(false)}
                  >
                    Profile
                  </Link>
                  {currentUser ? (
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50"
                    >
                      Logout
                    </button>
                  ) : (
                    <>
                      <Link
                        to="/login"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50"
                        onClick={() => setIsProfileDropdownOpen(false)}
                      >
                        Login
                      </Link>
                      <Link
                        to="/signup"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50"
                        onClick={() => setIsProfileDropdownOpen(false)}
                      >
                        Sign Up
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            {currentUser && (
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center mr-3">
                <span className="font-medium">
                  {userData?.name?.charAt(0) || currentUser?.email?.charAt(0).toUpperCase() || 'P'}
                </span>
              </div>
            )}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-white focus:outline-none p-1 rounded-md hover:bg-white/10"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden pt-3 pb-2 space-y-1">
            <Link
              to="/"
              className="block hover:bg-white/10 px-4 py-3 rounded-lg transition duration-300 font-medium"
              onClick={() => setIsMenuOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/quiz"
              className="block hover:bg-white/10 px-4 py-3 rounded-lg transition duration-300 font-medium"
              onClick={() => setIsMenuOpen(false)}
            >
              Quiz
            </Link>
            <Link
              to="/all-flashcards"
              className="block hover:bg-white/10 px-4 py-3 rounded-lg transition duration-300 font-medium"
              onClick={() => setIsMenuOpen(false)}
            >
              Flashcards
            </Link>
            <Link
              to="/create"
              className="block hover:bg-white/10 px-4 py-3 rounded-lg transition duration-300 font-medium"
              onClick={() => setIsMenuOpen(false)}
            >
              Create
            </Link>
            <Link
              to="/profile"
              className="block hover:bg-white/10 px-4 py-3 rounded-lg transition duration-300 font-medium"
              onClick={() => setIsMenuOpen(false)}
            >
              Profile
            </Link>
            {currentUser ? (
              <button
                onClick={handleLogout}
                className="block w-full text-left hover:bg-white/10 px-4 py-3 rounded-lg transition duration-300 font-medium"
              >
                Logout
              </button>
            ) : (
              <>
                <Link
                  to="/login"
                  className="block hover:bg-white/10 px-4 py-3 rounded-lg transition duration-300 font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="block hover:bg-white/10 px-4 py-3 rounded-lg transition duration-300 font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Sign Up
                </Link>
              </>
            )}
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;